import { Button, TextField } from '@mui/material';
import React, { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { EMAIL_PATTERN } from '../../../shared/consts';
import { PasswordInput, useAlert } from '../../../shared/components';
import { MainFrame } from '../components/main-frame';
import { withNext } from '../components/auth-links';
import { firebaseAuth } from '../../../shared/lib';
import { useSelector } from 'react-redux';
import { Store } from '../../../redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { parseRoomInput } from '@/screens/guest/lib/invite';
import { useMutation } from 'react-query';
import { UserCredential, signInWithEmailAndPassword } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { LoggerService } from '../../../shared/logger';
import { CustomDividerWithText } from '../components/custom-divider';
import { ContinueWithGoogleButton } from '../components/continue-with-google-button';
import { ContinueWithAppleButton } from '../components/continue-with-apple-button';
import { Capacitor } from '@capacitor/core';
import { HOME_ROUTE } from '../../authenticated/home';
import { IN_PLAY_ROUTE } from '../../authenticated/in-play';
import { DownloadLink } from './components';

export declare type SignInInput = {
  email: string;
  password: string;
};

export interface SignInPageProps {
  texts?: Record<string, string>;
}

export const useSignIn = () =>
  useMutation<UserCredential, FirebaseError, SignInInput>({
    mutationKey: 'sign-in',
    mutationFn: (data: SignInInput) => {
      return signInWithEmailAndPassword(
        firebaseAuth,
        data.email,
        data.password
      );
    },
  });

export const SignInPage: FC<SignInPageProps> = ({
  texts = {
    signInTitle: 'Welcome to Bhabi',
    button: 'Sign In',
  },
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<SignInInput>({
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const { mutate: signIn, isLoading: isSigningIn } = useSignIn();

  const { addAlert } = useAlert();

  const user = useSelector((state: Store) => state.userReducer);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const invitation = parseRoomInput(searchParams.get('next') || '');

  useEffect(() => {
    if (!user.activatedAt.Valid && user.isAuthenticated) {
      nav('/verify-email');
      return;
    }

    if (user.isAuthenticated && invitation) {
      nav(`/join/${invitation}`, { replace: true });
      return;
    }

    if (user.isAuthenticated && user.isInRoom) {
      nav(IN_PLAY_ROUTE.ABSOLUTE_PATH);
      return;
    }

    if (user.isAuthenticated) {
      nav(HOME_ROUTE.ABSOLUTE_PATH);
      return;
    }
  }, [nav, user, invitation]);

  const onSubmit: SubmitHandler<SignInInput> = data => {
    signIn(
      { email: data.email.trim().toLowerCase(), password: data.password },
      {
        onError: err => {
          LoggerService.error(err);
          addAlert({
            type: 'error',
            message: 'Failed to Sign In. Invalid email or password.',
          });
        },
        onSuccess: () => {
          reset();
        },
      }
    );
  };

  return (
    <>
      <DownloadLink />
      <MainFrame
        eyebrow={invitation ? 'You have a seat waiting' : 'Welcome back'}
        title={texts.signInTitle}
        documentTitle="Sign in"
        subtitle={
          invitation
            ? 'Sign in and we’ll take you straight to your table.'
            : 'Sign in for coins, friends and invites.'
        }
        stackProps={{ onSubmit: handleSubmit(onSubmit) }}
        linkPath={withNext('/sign-up', searchParams.get('next'))}
        forgotPasswordPath="/reset-password"
        texts={{
          linkMessage: 'New to Bhabi? Create an account',
          forgotPassword: 'Forgot password?',
        }}
        otherSignInOptions={
          <>
            <CustomDividerWithText>or</CustomDividerWithText>
            <div className="bh-auth__alt-row">
              <ContinueWithGoogleButton />
              {Capacitor.getPlatform() === 'ios' && <ContinueWithAppleButton />}
            </div>
            <p
              className="bh-small bh-muted"
              style={{ margin: '4px 0 0', textAlign: 'center' }}
            >
              Just want a quick game?{' '}
              <Link className="bh-link" to="/">
                Play as a guest
              </Link>
            </p>
          </>
        }
      >
        <TextField
          {...register('email', {
            required: `Email is required.`,
            pattern: EMAIL_PATTERN,
          })}
          label="Email"
          type="email"
          autoComplete="email"
          inputMode="email"
          error={!!errors.email}
          helperText={errors.email?.message || undefined}
          fullWidth
        />
        <PasswordInput<SignInInput>
          controllerProps={{
            control,
            name: 'password',
            rules: {
              required: `Password is required.`,
            },
          }}
          textFieldProps={{
            label: 'Password',
            autoComplete: 'current-password',
            error: !!errors.password,
            helperText: errors.password?.message || undefined,
            fullWidth: true,
          }}
        />
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isSigningIn}
        >
          {isSigningIn ? 'Signing in…' : texts.button}
        </Button>
      </MainFrame>
    </>
  );
};
